<template>
  
  <div>
    <b-breadcrumb>
      <b-breadcrumb-item>Administratie</b-breadcrumb-item>
      <b-breadcrumb-item to="/main/admin/contracts" active>Contracten</b-breadcrumb-item>
    </b-breadcrumb>

    <div class="d-flex align-items-center justify-content-between">
      <h2>Contracten</h2>
      <b-btn variant="success" to="/main/admin/contracts/create"><span class="mdi mdi-plus-thick" /> Contract toevoegen</b-btn>
    </div>

    <table-wrap :query="$options.query" :fields="$options.fields" hover table-class="contract-list" selectable :per-page="$store.state.perPage"
      :tbody-tr-class="rowClass" :state.sync="state" :reset-state="$store.state.resetContractsView"
                @row-selected="onRowSelected" small>

      <template #cell(selected)="data">
        <b-form-checkbox :checked="data.rowSelected"/>
      </template>

      <template v-slot:cell(regularisation)="row">
        <template v-if="row.item.regularisation">
          <span class="mdi mdi-check"/> Ja
        </template>
        <template v-else>
          <span class="mdi mdi-close" /> Nee
        </template>
      </template>

      <template v-slot:cell(renting)="row">
        <template v-if="row.item.renting">
          <span class="mdi mdi-check"/> Ja
        </template>
        <template v-else>
          <span class="mdi mdi-close" /> Nee
        </template>
      </template>
      <template v-slot:cell(actions)="row">
        <b-dropdown right text="Meer" size="sm" variant="primary" class="mr-1">
          <b-dropdown-item :to="`/main/admin/contracts/edit/${row.item.id}`"><span class="mdi mdi-pencil"/> Bewerken</b-dropdown-item>
          <b-dropdown-item @click="objToDelete = row">
            <span class="text-danger">
              <span class="mdi mdi-delete"/> Verwijderen
            </span>
          </b-dropdown-item>
        </b-dropdown>
        <b-btn variant="primary" size="sm" :to="`/main/admin/contracts/view/${row.item.id}`"><i class="mdi mdi-eye-outline"/> Bekijken</b-btn>
        <b-btn variant="primary" size="sm" class="ml-1" :to="`/main/admin/contracts/createInvoice/${row.item.id}`">
          <span class="mdi mdi-rocket"/> Factuur maken
        </b-btn>

      </template>

      <template #cell(invoices)="{item: { invoices: { nodes: [inv]} }}">
        <a :href="inv.sourceUrl" target="_blank" v-if="inv">
          Afrekening Q{{inv.quarter}} {{inv.year}}<span class="d-none d-xl2-inline"> gemaakt op {{inv.created|format_moment('YYYY-MM-DD')}}</span>
          <span class="mdi mdi-open-in-new"/>
        </a>
        <span v-else class="text-muted font-italic">Geen laatste factuur geregistreerd</span>
      </template>

      <template #custom-foot>
        <b-tr>
          <b-td class="p-1 pl-2 text-center"><b-icon icon="arrow-return-right"/></b-td>
          <b-td colspan="8" class="p-1">
            <b-btn size="sm" variant="primary" @click="priceIndexation"><span class="mdi mdi-ticket-percent-outline"/> Prijsindexatie</b-btn>
          </b-td>
        </b-tr>
      </template>

    </table-wrap>

    <b-modal centered title="Contract verwijderen" title-class="font-weight-bold" v-model="showDelete" lazy>
      <template #default v-if="objToDelete">
        Ben je zeker dat je contract <code>{{objToDelete.item.name}}</code>
        van klant <code>{{objToDelete.item.customer.name}}</code> wil verwijderen?
      </template>
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-btn variant="secondary" @click="cancel">Annuleren</b-btn>
        <busy-btn variant="danger" @click="okDelete(ok)" :busy="deleteBusy">Verwijderen</busy-btn>
      </template>
    </b-modal>

  </div>
  
</template>

<script>
import moment from 'moment';
import TableWrap from '@/components/TableWrap';
import {M_UPDATE_CONTRACTS_VIEW} from '@/lib/constants';
import vuex_computed from '@/lib/vuex_computed';
import BusyBtn from '@/components/BusyBtn';

export default
{
    components: { BusyBtn, TableWrap },
    computed:
    {
        state: vuex_computed('contractsView', M_UPDATE_CONTRACTS_VIEW),
        showDelete:
        {
            get() { return Boolean(this.objToDelete); },
            set(val) { this.objToDelete = val ? val : null; }
        }
    },
    data()
    {
        return {
            objToDelete: null,
            deleteBusy: false,
            selectedRows: []
        }
    },
    methods:
    {
        rowClass(item)
        {
            if(item === null)
                return;

            if(item.terminated)
                return 'contract-terminated-row';
        },
        async okDelete(ok)
        {
            this.deleteBusy = true;

            try
            {
                await this.$apollo.mutate(
                    {
                        mutation: require('../../graphql/mutation/delete_contract.gql'),
                        variables: { id: this.objToDelete.item.id }
                    }
                );
            }
            catch (e)
            {
                console.error(e);
            }
            finally
            {
                this.deleteBusy = false;
                ok();
            }
        },
        onRowSelected(rows)
        {
            this.selectedRows = rows;

        },
        async priceIndexation()
        {
            const contractIds = this.selectedRows.map(r => r.contractId);
            await this.$router.push(
                {
                    path: '/main/admin/priceIndexation',
                    query: { contractIds }
                }
            );
        },
    },
    fields:
    [
        {
            key: 'selected',
            label: '',
            tdClass: 'p-1',
            selectAll: true,
            thClass: 'p-0'
        },
        {
            key: 'name',
            search: 'STRING',
            sort: 'NAME',
            sortable: true,
            tdClass: 'p-1',
            label: 'Naam'
        },
        {
            key: 'customer.name',
            label: 'Klant',
            tdClass: 'p-1',
            search: 'STRING'
        },
        {
            key: 'start',
            formatter(v)
            {
                return moment(v).format('YYYY-MM-DD')
            },
            tdClass: 'text-nowrap',
            sortable: true,
            sort: 'START',
        },
        {
            key: 'regularisation',
            label: 'Regularisatie',
            tdClass: 'p-1',
            search: 'BOOLEAN'
        },
        {
            key: 'renting',
            label: 'Renting',
            tdClass: 'p-1',
            search: 'BOOLEAN'
        },
        {
            key: 'invoices',
            label: 'Laatste factuur',
            tdClass: 'p-1'
        },
        {
            key: 'actions',
            label: 'Acties',
            tdClass: 'text-right p-1',
            thClass: 'text-right'
        },
    ],
    query: require('../../graphql/listContracts.query.gql')
}
</script>

<style>
  table.contract-list { table-layout: fixed; }

  table.contract-list th:nth-child(1) { width: 20px; } /* Selected checkbox */
  table.contract-list th:nth-child(2) { width: 150px; } /* Contract Id */
  table.contract-list th:nth-child(3) { width: 30%; } /* Company name */
  table.contract-list th:nth-child(4) { width: 7em; } /* Start date */
  table.contract-list th:nth-child(5) { width: 7em; } /* Regularisation */
  table.contract-list th:nth-child(6) { width: 7em; } /* Renting */
  table.contract-list th:nth-child(7) { width: 40%; } /* Last invoice */
  table.contract-list th:nth-child(8) { width: 300px; } /* Actions */

  table.contract-list tbody td:nth-child(2) { font-family: Consolas, monospace; }
  /*table.contract-list tbody td:nth-child(2) { font-family: Consolas, monospace; }*/

  table.contract-list tbody tr.contract-terminated-row { color: lightgray; }

</style>